import { createContext, useContext, useEffect, useState, useRef } from 'react';
import type { ReactNode } from 'react';
import type { Product } from '../../types/product';
import { db } from '../../firebase/config';
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  getDocs,
  type DocumentData,
} from 'firebase/firestore';

export interface ProductsContextType {
  products: Product[];
  loading: boolean;
  error: string | null;
  addProduct: (product: Omit<Product, 'id'>) => Promise<void>;
  updateProduct: (id: number, updates: Partial<Product>) => Promise<void>;
  deleteProduct: (id: number) => Promise<void>;
  getProductById: (id: number) => Product | undefined;
  setProducts: (products: Product[]) => void;
}

const ProductsContext = createContext<ProductsContextType | undefined>(undefined);

export const ProductsProvider = ({ children }: { children: ReactNode }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Sample products for development
  const sampleProducts: Product[] = [
    {
      id: 1,
      name: 'Premium Surfboard',
      description: 'High-performance surfboard for professional surfers',
      price: 899.99,
      originalPrice: 999.99,
      image: 'https://images.unsplash.com/photo-1565995240383-ed5204015aee?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      images: [
        'https://images.unsplash.com/photo-1565995240383-ed5204015aee?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80'
      ],
      category: 'Sports',
      stock: 10,
      isInStock: true,
      isFeatured: true,
      isNew: true,
      rating: 4.8,
      reviewCount: 24,
      createdAt: new Date('2023-01-15'),
      updatedAt: new Date('2023-01-15')
    },
    {
      id: 2,
      name: 'Beginner Windsurfing Kit',
      description: 'Complete windsurfing package for beginners',
      price: 1299.99,
      image: 'https://images.unsplash.com/photo-1530103862676-de8c6de21652?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      images: [
        'https://images.unsplash.com/photo-1530103862676-de8c6de21652?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80'
      ],
      category: 'Sports',
      stock: 5,
      isInStock: true,
      isFeatured: true,
      rating: 4.5,
      reviewCount: 15,
      createdAt: new Date('2023-02-20'),
      updatedAt: new Date('2023-02-20')
    },
    {
      id: 3,
      name: 'Wireless Earbuds Pro',
      description: 'High-quality wireless earbuds with noise cancellation',
      price: 199.99,
      originalPrice: 249.99,
      image: 'https://images.unsplash.com/photo-1590658268037-6bf12165a8df?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      category: 'Electronics',
      stock: 25,
      isInStock: true,
      isNew: true,
      rating: 4.7,
      reviewCount: 142,
      createdAt: new Date('2023-03-15')
    },
    {
      id: 4,
      name: 'Classic Denim Jacket',
      description: 'Timeless denim jacket for any casual occasion',
      price: 79.99,
      image: 'https://images.unsplash.com/photo-1542272604-787c3835535d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      category: 'Clothing',
      stock: 30,
      isInStock: true,
      isFeatured: true,
      rating: 4.5,
      reviewCount: 89,
      createdAt: new Date('2023-02-01')
    },
    {
      id: 5,
      name: 'Running Shoes',
      description: 'Lightweight running shoes with superior cushioning',
      price: 129.99,
      originalPrice: 149.99,
      image: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      category: 'Footwear',
      stock: 15,
      isInStock: true,
      isFeatured: true,
      rating: 4.8,
      reviewCount: 210,
      createdAt: new Date('2023-01-20')
    },
    {
      id: 6,
      name: 'Leather Wallet',
      description: 'Genuine leather wallet with multiple card slots',
      price: 49.99,
      image: 'https://images.unsplash.com/photo-1548032885-b5e38734688a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      category: 'Accessories',
      stock: 50,
      isInStock: true,
      rating: 4.6,
      reviewCount: 75,
      createdAt: new Date('2023-03-01')
    },
    {
      id: 7,
      name: 'Indoor Plant Set',
      description: 'Set of 3 low-maintenance indoor plants',
      price: 59.99,
      image: 'https://images.unsplash.com/photo-1485955900956-50d97d3a4a9b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      category: 'Home & Garden',
      stock: 18,
      isInStock: true,
      isNew: true,
      rating: 4.7,
      reviewCount: 63,
      createdAt: new Date('2023-04-10')
    },
    {
      id: 8,
      name: 'Skincare Gift Set',
      description: 'Complete skincare routine in one set',
      price: 89.99,
      originalPrice: 119.99,
      image: 'https://images.unsplash.com/photo-1596755094514-f87e34085b2c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      category: 'Beauty',
      stock: 22,
      isInStock: true,
      isFeatured: true,
      rating: 4.9,
      reviewCount: 134,
      createdAt: new Date('2023-02-15')
    },
    {
      id: 9,
      name: 'Robot Building Kit',
      description: 'Build your own programmable robot',
      price: 129.99,
      image: 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      category: 'Toys',
      stock: 12,
      isInStock: true,
      isNew: true,
      rating: 4.8,
      reviewCount: 47,
      createdAt: new Date('2023-04-05')
    },
    {
      id: 10,
      name: 'Bestseller Novel',
      description: 'Latest bestselling novel by a renowned author',
      price: 24.99,
      originalPrice: 29.99,
      image: 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      category: 'Books',
      stock: 35,
      isInStock: true,
      rating: 4.6,
      reviewCount: 218,
      createdAt: new Date('2023-01-10')
    }
  ];

  // Firestore real-time subscription and optional seeding
  const seededRef = useRef(false);

  useEffect(() => {
    setLoading(true);
    const col = collection(db, 'products');
    const qy = query(col, orderBy('id'));
    const unsubscribe = onSnapshot(
      qy,
      async (snapshot) => {
        try {
          if (snapshot.empty && !seededRef.current) {
            // One-time seed with sample data to bootstrap demo
            seededRef.current = true;
            await Promise.all(
              sampleProducts.map((p) =>
                addDoc(col, {
                  ...p,
                  // Store dates as server timestamps
                  createdAt: serverTimestamp(),
                  updatedAt: serverTimestamp(),
                })
              )
            );
            return; // next snapshot will populate
          }

          const list: Product[] = snapshot.docs.map((doc) => {
            const data = doc.data() as DocumentData;
            return {
              id: data.id as number,
              name: data.name as string,
              description: data.description as string,
              brand: data.brand,
              subcategory: data.subcategory,
              audience: data.audience,
              price: data.price as number,
              originalPrice: data.originalPrice,
              isOnSale: data.isOnSale,
              image: data.image as string,
              images: data.images,
              category: data.category as Product['category'],
              tags: data.tags,
              sku: data.sku,
              stock: data.stock as number,
              isInStock: data.isInStock as boolean,
              isFeatured: data.isFeatured,
              isNew: data.isNew,
              rating: data.rating,
              reviewCount: data.reviewCount,
              url: data.url,
              createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : undefined,
              updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : undefined,
            } as Product;
          });
          setProducts(list);
          setError(null);
        } catch (err) {
          console.error('Error mapping products snapshot:', err);
          setError('Failed to read products');
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        console.error('Products subscription error:', err);
        setError('Failed to subscribe to products');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const addProduct = async (product: Omit<Product, 'id'>) => {
    try {
      const col = collection(db, 'products');
      const nextId = Math.max(0, ...products.map((p) => p.id)) + 1;
      const sanitized: Record<string, unknown> = Object.fromEntries(
        Object.entries({
          ...product,
          id: nextId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }).filter(([_, v]) => v !== undefined)
      );
      await addDoc(col, sanitized as any);
    } catch (err) {
      console.error('Failed to add product:', err);
      setError('Failed to add product');
    }
  };

  const updateProduct = async (id: number, updates: Partial<Product>) => {
    try {
      const col = collection(db, 'products');
      const qy = query(col, where('id', '==', id));
      const snap = await getDocs(qy);
      if (snap.empty) {
        throw new Error(`Product with id ${id} not found`);
      }
      const docRef = snap.docs[0].ref;
      const { id: _omit, createdAt: _c, updatedAt: _u, ...rest } = updates as any;
      const payload: Record<string, unknown> = Object.fromEntries(
        Object.entries({
          ...rest,
          updatedAt: serverTimestamp(),
        }).filter(([_, v]) => v !== undefined)
      );
      // Optimistic update for smoother UX
      setProducts(prev => prev.map(p => p.id === id ? { ...p, ...(updates as Product), updatedAt: new Date() } : p));
      await updateDoc(docRef, payload as any);
    } catch (err) {
      console.error('Failed to update product:', err);
      setError('Failed to update product');
    }
  };

  const deleteProduct = async (id: number) => {
    try {
      const col = collection(db, 'products');
      const qy = query(col, where('id', '==', id));
      const snap = await getDocs(qy);
      if (snap.empty) {
        throw new Error(`Product with id ${id} not found`);
      }
      await deleteDoc(snap.docs[0].ref);
    } catch (err) {
      console.error('Failed to delete product:', err);
      setError('Failed to delete product');
    }
  };

  const getProductById = (id: number) => {
    return products.find(product => product.id === id);
  };

  const contextValue: ProductsContextType = {
    products,
    loading,
    error,
    addProduct,
    updateProduct,
    deleteProduct,
    getProductById,
    setProducts,
  };

  return (
    <ProductsContext.Provider value={contextValue}>
      {children}
    </ProductsContext.Provider>
  );
};

export const useProducts = (): ProductsContextType => {
  const context = useContext(ProductsContext);
  if (context === undefined) {
    throw new Error('useProducts must be used within a ProductsProvider');
  }
  return context;
};

export default ProductsContext;
